import React from 'react';
import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { type AskResponse } from '@cotera/client/v0/actions/framework/types';
import { getDeveloperDetailsFromInScopeModels } from '@cotera/client/v0/actions/framework/developer-details';
import { DeveloperDetailsModal } from './developer-details-modal';

const emptySchema = z.object({});
type Params = z.infer<typeof emptySchema>;

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
};

/**
 * Command-palette action that surfaces key-value developer details from every
 * in-scope model that implements {@link ModelWithDeveloperDetails}. Sections are
 * ordered newest model first.
 */
export class ShowDeveloperDetailsAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Developer details';
  description = 'View identifiers and debug info for the current page';
  inputSchema = emptySchema;
  group = 'Developer Tools';
  icon = 'code-bracket';

  constructor(private readonly modalManager: ModalManager) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return (
      getDeveloperDetailsFromInScopeModels(context.getAllInScopeModels())
        .length > 0
    );
  }

  shouldAsk(): boolean {
    return true;
  }

  ask(_params: Params, context: ExecuteContext): AskResponse<Params> {
    const sections = getDeveloperDetailsFromInScopeModels(
      context.getAllInScopeModels()
    );

    return {
      view: ({ onCancel }) => {
        this.modalManager.push({
          view: () => <DeveloperDetailsModal sections={sections} />,
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }

  async execute(
    _payload: Params,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    return ok({});
  }
}
